import { Autocomplete, Box, Button, Dialog, FormControl, FormLabel, IconButton, Input, TextField } from "@mui/material"
import { FullSizeTextarea } from "styles/styledComponents"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import { useCallback, useEffect, useState } from "react"
import { FormButtons, MyDialog } from "./MyDialog"
import { deleteItem, getAddons, getChoices, postItem, putItem } from "helpers/api"
import { useAlert } from "contexts/alertContext"
import { TranslationForm } from "./TranslationForm"
import MyTabs from "./MyTabs"
import { DishAddonForm } from "./DishAddonForm"
import { IoLanguage } from "react-icons/io5"
import { useTranslation } from "react-i18next"
import { useStateContext } from "contexts/userContext"
import useImageGeneration from "hooks/useImageGeneration"
import Alert from "@mui/material/Alert"
import CameraAltIcon from "@mui/icons-material/CameraAlt";

const maxAttempts = 10
export function DishForm({ restaurant_id, data, groups, onClose, onSuccess, onDelete }) {
  const { generateImage, loading: imageGenerating, error: imageError } = useImageGeneration()

  const [formState, setFormState] = useState(data)
  const [errorAlertId, setErrorAlertId] = useState(null)
  const [isLoading, setIsLoading] = useState(null)
  const [translationProp, setTranslationProp] = useState(null)
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false)

  const { setSuccessAlert, setErrorAlert, closeAlert } = useAlert()

  const { t } = useTranslation()
  const { language } = useStateContext()

  useEffect(() => {
    getAddons(data.id).then((addons) => {
      setFormState((curFormState) => ({ ...curFormState, addons }))
    })
    getChoices(data.id).then((choices) => {
      setFormState((curFormState) => ({ ...curFormState, choices }))
    })
  }, [])

  const getAiAttempts = () => {
    const attempts = localStorage.getItem("aiAttempts")
    return attempts ? parseInt(attempts) : 0
  }

  const updateAiAttempts = (attempts) => {
    localStorage.setItem("aiAttempts", attempts)
  }

  //original
  const handleEditChange = useCallback((name, value) => {
    setFormState((currentState) => ({ ...currentState, [name]: value }))
  }, [])

  const validateForm = useCallback(
    (item) => {
      // Проверяем, есть ли название: либо в базовом поле title, либо хотя бы в одном из переводов
      const hasTitle = item.title?.trim() ||
        (item.translations && Object.values(item.translations).some(t => t?.trim()))

      if (!hasTitle || !item.price) {
        setErrorAlertId(setErrorAlert(t("Please fill Title andPrice fields")))

        return false
      }

      const price = parseFloat(item.price)
      if (!price || price <= 0) {
        setErrorAlertId(setErrorAlert(t("The Price field must contain number more than 0")))

        return false
      }

      if (errorAlertId !== null) {
        closeAlert(errorAlertId)
        setErrorAlertId(null)
      }

      return true
    },
    [errorAlertId]
  )

  const handleDelete = useCallback(async () => {
    await deleteItem(data.id)
    onDelete(data.id)

    setSuccessAlert(t("Object deleted successful", { name: data.title }))
  }, [data, onDelete])

  const onSave = useCallback(async () => {
    if (!validateForm(formState)) return

    setIsLoading(true)

    // Подготавливаем данные для отправки
    // Если базовое поле title пустое, но есть переводы, используем первый непустой перевод
    const dataToSend = { ...formState }
    if (!dataToSend.title?.trim() && dataToSend.translations) {
      const firstTranslation = Object.values(dataToSend.translations).find(t => t?.trim())
      if (firstTranslation) {
        dataToSend.title = firstTranslation
      }
    }

    if (formState.id) {
      await putItem(formState.id, dataToSend)
      setIsLoading(false)

      onSuccess(dataToSend)
      setSuccessAlert(t("Object updated successful", { name: data.title }))

      return
    }

    const responseData = await postItem({
      ...dataToSend,
      restaurant_id: restaurant_id,
    })
    setIsLoading(false)

    onSuccess({ ...dataToSend, id: responseData.insertId })

    setSuccessAlert(t("Object created successful", { name: data.title }))
  }, [formState])

  const handleGenerateImage = async () => {
    const attemptsUsed = getAiAttempts()

    if (attemptsUsed >= maxAttempts) {
      setErrorAlertId(setErrorAlert(t("You have used all your free attempts.")))
      return
    }

    // Увеличиваем счетчик попыток в localStorage
    updateAiAttempts(attemptsUsed + 1)

    const title = formState.translations?.[language]?.trim()
      ? formState.translations[language]
      : formState.title?.trim() || ""

    const description = formState.translations_descriptions?.[language]?.trim()
      ? formState.translations_descriptions[language]
      : formState.description?.trim() || ""

    const prompt = `food photo, professional restaurant menu photo of ${title}, ${description ? `, ${description}` : ""}, high quality, photorealistic`

    const generatedImage = await generateImage(prompt)
    if (generatedImage) {
      handleEditChange("image", generatedImage)
    }
  }
  const generalTabContent = (
    <div className="my-form flex-column g15" style={{ width: "600px", maxWidth: "100%" }}>
      <FormControl required fullWidth>
        <FormLabel>{t("Name")}</FormLabel>
        <div className="flex-row flex-y-center g10">
          <TextField
            value={
              formState.translations?.[language]?.trim()
                ? formState.translations[language]
                : formState.title || ""
            }
            onChange={(e) => {
              console.log("e.target.value", e.target.value)

              // Если для текущего языка есть непустой перевод, обновляем его
              if (formState.translations?.[language]?.trim()) {
                setFormState((currentState) => ({
                  ...currentState,
                  translations: {
                    ...currentState.translations,
                    [language]: e.target.value
                  }
                }))
              } else {
                // Иначе обновляем базовое поле title
                handleEditChange("title", e.target.value)
              }
            }}
            fullWidth
          />

          {/* original */}

          {/* <TextField
            value={
              formState.translations?.[language]?.trim()
                ? formState.translations[language]
                : formState.title?.trim() || ""
            }
            onChange={(e) => {
              console.log("e.target.value", e.target.value)
              handleEditChange("title", e.target.value)
            }}
            fullWidth
          /> */}

          <IconButton onClick={() => setTranslationProp("translations")}>
            <IoLanguage />
          </IconButton>
        </div>
      </FormControl>

      <FormControl fullWidth>
        <FormLabel>{t("Description")}</FormLabel>
        <div className="flex-row g10">
          <FullSizeTextarea
            // value={formState.description || ""}

            value={
              formState.translations_descriptions?.[language] !== undefined && formState.translations_descriptions?.[language] !== null
                ? formState.translations_descriptions[language]
                : formState.description || ""
            }
            onChange={(e) => {
              console.log("e.target.value", e.target.value)

              // Если есть перевод для текущего языка, обновляем его
              if (formState.translations_descriptions?.[language] !== undefined) {
                setFormState((currentState) => ({
                  ...currentState,
                  translations_descriptions: {
                    ...currentState.translations_descriptions,
                    [language]: e.target.value
                  }
                }))
              } else {
                // Иначе обновляем базовое поле description
                handleEditChange("description", e.target.value)
              }
            }}
            label={t("Description")}
          />

          <div>
            <IconButton onClick={() => setTranslationProp("translations_descriptions")}>
              <IoLanguage />
            </IconButton>
          </div>
        </div>
      </FormControl>

      <FormControl required fullWidth>
        <FormLabel>{t("Price")}</FormLabel>
        <TextField
          type="number"
          InputProps={{
            inputProps: { step: 0.1, min: 0 }, // Установка атрибутов input
            style: { width: "200px" },
          }}
          value={formState.price || ""}
          onChange={(e) => handleEditChange("price", e.target.value)}
          placeholder="0.00"
        />
      </FormControl>

      {/* old */}

      <FormControl fullWidth>
  <FormLabel>{t("Image Preview")}</FormLabel>
  <Box>
    {formState.image ? (
      <div className="mb5">
        <img
          src={formState.image}
          style={{ maxWidth: "200px", maxHeight: "160px", cursor: "zoom-in" }}
          alt=" "
          onClick={() => setIsImagePreviewOpen(true)}
          title={t("Click to view full size")}
        />
      </div>
    ) : null}

    <div className="flex-row g10">
      {/* Кнопка для выбора фото из галереи */}
      <label htmlFor="imgUploadGallery">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            borderRadius: 8,
            backgroundColor: "#2196f3",
            color: "#fff",
            "&:hover": { backgroundColor: "#1976d2" },
          }}
        >
          {formState.image ? t("Upload new") : t("Upload from gallery")}
        </Button>
      </label>

      {/* Кнопка для съемки фото с камеры (только на мобильных устройствах) */}
      {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
        <label htmlFor="imgUploadCamera">
          <Button
            variant="contained"
            component="span"
            startIcon={<CameraAltIcon />}
            sx={{
              borderRadius: 8,
              backgroundColor: "#4CAF50",
              color: "#fff",
              "&:hover": { backgroundColor: "#388E3C" },
            }}
          >
            {t("Take a photo")}
          </Button>
        </label>
      )}

      {/* Кнопка генерации AI-изображения */}
      <Button
        variant="contained"
        onClick={handleGenerateImage}
        disabled={imageGenerating || !formState.title || getAiAttempts() >= maxAttempts} // Блокируем, если исчерпаны попытки
        sx={{
          borderRadius: 8,
          backgroundColor: "#FF9800",
          color: "#fff",
          "&:hover": { backgroundColor: "#F57C00" },
        }}
      >
        {imageGenerating ? t("Generating...") : t("Generate AI Image")}
      </Button>
    </div>

    {/* Input для загрузки изображения из галереи */}
    <Input
      id="imgUploadGallery"
      type="file"
      inputProps={{
        accept: "image/*",
        style: { display: "none" },
      }}
      onChange={(e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          handleEditChange("image", reader.result);
        };
      }}
      required
    />

    {/* Input для камеры (только на мобильных) */}
    {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
      <Input
        id="imgUploadCamera"
        type="file"
        inputProps={{
          accept: "image/*",
          capture: "camera",
          style: { display: "none" },
        }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (!file) return;

          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = () => {
            handleEditChange("image", reader.result);
          };
        }}
        required
      />
    )}

    {imageError && (
      <Alert severity="error" sx={{ mt: 1 }}>
        {imageError}
      </Alert>
    )}
  </Box>
</FormControl>

      {groups && (
        <FormControl fullWidth>
          <FormLabel>{t("Group")}</FormLabel>
          <Autocomplete
            options={groups}
            getOptionLabel={(option) => option.name}
            value={groups.find((group_obj) => group_obj.id === formState.group_obj?.id) || null}
            filterSelectedOptions
            onChange={(event, newValue) => {
              handleEditChange("group_obj", newValue)
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      )}
    </div>
  )

  const choicesTabContent = (
    <div
      style={{
        width: "600px",
        maxWidth: "100%",
        minHeight: "608px",
      }}
    >
      {formState.choices ? (
        <DishAddonForm
          newItemName={t("New choice")}
          addingBtnText={t("Add choice")}
          items={formState.choices}
          onChange={(newValue) => {
            handleEditChange("choices", newValue)
          }}
        />
      ) : null}
    </div>
  )

  const addonsTabContent = (
    <div
      style={{
        width: "600px",
        maxWidth: "100%",
        minHeight: "608px",
      }}
    >
      {formState.addons ? (
        <DishAddonForm
          newItemName={t("New addon")}
          addingBtnText={t("Add addon")}
          withMaxSelectedField={true}
          items={formState.addons}
          onChange={(newValue) => {
            handleEditChange("addons", newValue)
          }}
        />
      ) : null}
    </div>
  )

  return (
    <>
      <MyDialog
        isOpen={true}
        // title={t("Creating dish")}
        // title={formState.title || t("Creating dish")}
        title={formState.title ? "" : t("Creating item")}
        onClose={onClose}
        isLoading={isLoading}
        actions={<FormButtons {...{ onDelete: data.id ? handleDelete : null, onClose, onSave }} />}
      >
        <MyTabs
          tabs={[
            {
              label: t("General"),
              content: generalTabContent,
            },
            {
              label: t("Choices"),
              content: choicesTabContent,
            },
            {
              label: t("Addons"),
              content: addonsTabContent,
            },
          ]}
        />
      </MyDialog>

      <Dialog
        open={isImagePreviewOpen}
        onClose={() => setIsImagePreviewOpen(false)}
        maxWidth="lg"
      >
        <img
          src={formState.image}
          alt=" "
          style={{ display: "block", maxWidth: "90vw", maxHeight: "90vh", cursor: "zoom-out" }}
          onClick={() => setIsImagePreviewOpen(false)}
        />
      </Dialog>

      {translationProp ? (
        //object version
        <TranslationForm
          basePhrase={translationProp === "translations_descriptions" ? formState.description : formState.title}
          translations={formState[translationProp]}
          onClose={() => setTranslationProp(null)}
          onSave={(newTranslationsState) => {
            setFormState((curState) => {
              const updatedState = {
                ...curState,
                [translationProp]: newTranslationsState.translations,
              }

              if (translationProp === "translations_descriptions") {
                updatedState["description"] = newTranslationsState.basePhrase
              } else {
                updatedState["title"] = newTranslationsState.basePhrase
              }

              return updatedState
            })

            setTranslationProp(null)
          }}
        />
      ) : // original
      // <TranslationForm
      //   basePhrase={translationProp === "translations_descriptions" ? formState.description : formState.title}
      //   translations={formState[translationProp]}
      //   onClose={() => setTranslationProp(null)}
      //   onSave={(newTranslationsState) => {
      //     setFormState((curState) => ({
      //       ...curState,
      //       [translationProp]: newTranslationsState,

      //       // ["title"]: newTranslationsState.basePhrase,
      //       // ["description"]: newTranslationsState.basePhrase,

      //     }))
      //     setTranslationProp(null)
      //   }}
      // />

      // //all in version
      // <TranslationForm
      //   basePhrase={translationProp === "translations_descriptions" ? formState.description : formState.title}
      //   translations={formState[translationProp]}
      //   onClose={() => setTranslationProp(null)}
      //   onSave={(newTranslationsState) => {
      //     setFormState((curState) => ({
      //       ...curState,
      //       [translationProp]: newTranslationsState,
      //       ...(translationProp === "translations_descriptions"
      //         ? { description: newTranslationsState.basePhrase }
      //         : { title: newTranslationsState.basePhrase }),
      //     }))
      //     setTranslationProp(null)
      //   }}
      // />
      null}
    </>
  )
}
